import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    PUBLIC,
    PAYMENT_CURRENCY,
    PAYMENT_UPGRADE,
    PAYMENT_DOWNGRADE,
    PAYMENT_BUY,
} from "const"
import { MtPackage, MtCartCreate } from "server/models"
import { StateType, CartPackageAction } from "client/utils"
import {
    StoreType,
    LoadAvailablePackages,
    LoadAllowedPackagesForAccount,
    LoadAccountUsers,
    CreateCartData,
    ActivePackageDaysLeft,
    setBillingAvailablePackages,
    setBillingReloadCart,
} from "client/store"
import {
    CrossIcon,
    Loader,
    Feedback,
} from "client/components"
import {
    BillingModalWrapper,
    CloseButton,
    BillingModalContentWrapper,
    BillingModalTitle,
    BillingModalSubtitle,
    PaymentMessage,
} from "client/pages/mt/billing/styled"
import {
    PackageRow,
    PackageWrapper,
    PackageName,
    PackageRowDescription,
    PackageButtonWrapper,
    PackageBuyButton,
} from "./styled"


interface PackageModalProps {
    show: boolean
    onClose: () => void
}

const AvailablePackages = ({
    show,
    onClose,
}: PackageModalProps) => {
    const availablePackages = useSelector((state: StoreType) => state.billing.availablePackages)
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const systemActivePackage = useSelector((state: StoreType) => state.system.packageActive)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [error, setError] = useState<string>(null)

    useEffect(() => {
        setLoadState(PENDING)
        LoadAvailablePackages().then((res) => {
            LoadAllowedPackagesForAccount(userData.accountId).then((allowedPackageIds) => {
                if (allowedPackageIds.length) {
                    const allowedPackages = res.filter((row) => allowedPackageIds.includes(row.id))
                    dispatch(setBillingAvailablePackages(allowedPackages))
                } else {
                    const allowedPackages = res.filter((row) => row.visibility === PUBLIC)
                    dispatch(setBillingAvailablePackages(allowedPackages))
                }
                setLoadState(IDLE)
            })
        })
    }, [])

    const onBuy = (_package: MtPackage, action: CartPackageAction) => {
        setLoadState(PENDING)
        setError(null)

        LoadAccountUsers(userData.accountId).then((userIds) => {
            if (userIds.length > _package.maxUsers) {
                setLoadState(IDLE)
                setError(lang.buyPackageMaxUsersError.replace("{0}", userIds.length.toString()))
                return
            }

            const cartData: MtCartCreate = {
                accountId: userData.accountId,
                userId: userData.userId,
                packageIdFrom: systemActivePackage.packageId,
                packageIdTo: _package.id,
                packageAction: action,
                librarySetId: null,
                projectId: null,
            }
            CreateCartData(cartData).then(() => {
                setLoadState(IDLE)
                onClose()

                dispatch(setBillingReloadCart(false))
                setTimeout(() => {
                    dispatch(setBillingReloadCart(true))
                }, 0)
            })
        })
    }

    const rows: MtPackage[][] = []
    let packageRow: MtPackage[] = []
    let counter = 0
    availablePackages.forEach((_package) => {
        if (counter < 3) {
            packageRow.push(_package)
            counter += 1
        } else {
            rows.push(packageRow)
            counter = 0
            packageRow = []
        }
    })
    if (packageRow.length) rows.push(packageRow)

    const activePackage = packageList[systemActivePackage.packageId]
    const activePackagePrice = activePackage && Number(activePackage.price)

    const daysLeft = ActivePackageDaysLeft(systemActivePackage.dateEnd)
    const showGradeButton = activePackage && activePackage.id !== 1 && daysLeft !== 0 // not "demo"

    return (
        <BillingModalWrapper show={show}>
            <CloseButton onClick={onClose}>
                <CrossIcon />
            </CloseButton>
            <BillingModalContentWrapper>
                {
                    loadState === PENDING
                    && <Loader paddingTop={150} />
                }

                <BillingModalTitle>{lang.selectPackage}</BillingModalTitle>
                <BillingModalSubtitle>{lang.contactToPurchase}</BillingModalSubtitle>

                {
                    rows.map((row, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <PackageRow key={index}>
                            {
                                row.map((_package) => {
                                    const price = Number(_package.price)
                                    return (
                                        <PackageWrapper key={_package.id}>
                                            <PackageName>{_package.name}</PackageName>
                                            <PackageRowDescription>{`1 - ${_package.maxUsers} ${lang.users}`}</PackageRowDescription>
                                            <PackageRowDescription>{lang.packageDuration}</PackageRowDescription>

                                            <PackageButtonWrapper>
                                                {
                                                    showGradeButton
                                                    && activePackagePrice < price
                                                    && (
                                                        <PackageBuyButton onClick={() => onBuy(_package, PAYMENT_UPGRADE)}>
                                                            {`${lang.upgrade} ${_package.price - activePackage.price} ${PAYMENT_CURRENCY}`}
                                                        </PackageBuyButton>
                                                    )
                                                }
                                                {
                                                    showGradeButton
                                                    && activePackagePrice > price
                                                    && (
                                                        <PackageBuyButton onClick={() => onBuy(_package, PAYMENT_DOWNGRADE)}>
                                                            {lang.downgrade}
                                                        </PackageBuyButton>
                                                    )
                                                }
                                                <PackageBuyButton onClick={() => onBuy(_package, PAYMENT_BUY)}>
                                                    {`${lang.buy} ${_package.price} ${PAYMENT_CURRENCY}`}
                                                </PackageBuyButton>
                                            </PackageButtonWrapper>
                                        </PackageWrapper>
                                    )
                                })
                            }
                        </PackageRow>
                    ))
                }

                {
                    error && (
                        <PaymentMessage>
                            <Feedback>{error}</Feedback>
                        </PaymentMessage>
                    )
                }
            </BillingModalContentWrapper>
        </BillingModalWrapper>
    )
}
export default AvailablePackages
